import { captureException } from "@/error/sentry";
import type { BoardType, IdMap } from "@/model/baseTypes";
import type { Board, BoardData } from "@/model/board";

import { useArtStore } from "../art";
import { useFlexStore } from "../flex";
import { useTeamStore } from "../team";
import { emptyFlexBoard } from "./frontendBoard";

export interface BoardByTypeOptions {
  teamId?: string;
  artId?: string;
}

export function boardByType<T extends BoardType>(
  boards: IdMap<Board>,
  type: T,
  options: BoardByTypeOptions = {},
): BoardData<T> {
  const board = boardByTypeOptional(boards, type, options);
  if (board) {
    return board;
  }

  captureException(Error(`board type '${type}' not found`), {
    data: {
      info: {
        type,
        teamId: options.teamId,
        artId: options.artId,
        boards: Object.values(boards).map(({ id, type }) => ({ id, type })),
        team: useTeamStore().current,
        art: useArtStore().current,
      },
    },
  });

  return {} as BoardData<T>;
}

export function boardByTypeOptional<T extends BoardType>(
  boards: IdMap<Board>,
  type: T,
  { teamId, artId }: BoardByTypeOptions = {},
): BoardData<T> | undefined {
  const board = Object.values(boards).find((board) => {
    const isTypeMatch = board.type === type;
    const isTeamMatch = boardBelongsToTeam(board, teamId || useTeamStore().current.id);

    const isArtMatch = boardBelongsToArt(board, artId || useArtStore().current.id);
    const isFlexMatch = boardIsCurrentFlex(board, useFlexStore().currentFlexBoard);
    return isTypeMatch && isTeamMatch && isArtMatch && isFlexMatch;
  });

  if (board) {
    return board as BoardData<T>;
  }

  if (type === "flex" && useFlexStore().currentFlexBoard === null) {
    // boards are <KeepAlive> so FlexBoard is still alive even when the user deletes the last flex board
    return emptyFlexBoard() as unknown as BoardData<T>;
  }
}

function boardBelongsToTeam(board: Board, teamId?: string | null) {
  return board.type !== "team" || !teamId || board.team.id === teamId;
}

function boardBelongsToArt(board: Board, artId?: string) {
  return (
    ["team", "flex", "objectives"].includes(board.type) ||
    !board.artId ||
    !artId ||
    useArtStore().arts.length <= 1 ||
    board.artId === artId
  );
}

function boardIsCurrentFlex(board: Board, flexBoard: BoardData<"flex"> | null) {
  return board.type !== "flex" || !flexBoard || board.id === flexBoard.id;
}
